/**
 * DbCityPicker - 省市区三级联动选择器
 *
 * 特性：
 * - 纯 React Native 实现，无需额外原生依赖
 * - 支持省市区三级联动
 * - 底部弹窗交互
 * - 支持默认选中
 * - FlatList 滚轮选择，高性能
 * - 数据由外部传入，组件不包含 mock 数据
 */

import React, { useState, useCallback, useMemo, useEffect, forwardRef, useImperativeHandle, useRef } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  Modal,
  FlatList,
  NativeSyntheticEvent,
  NativeScrollEvent,
} from 'react-native';
import type { DbCityPickerProps, DbCityPickerRef, CityArea } from './DbCityPicker.types';
import { styles, ITEM_HEIGHT } from './DbCityPicker.styles';

// =====================================================
// 工具函数
// =====================================================

/** 根据省份编码获取城市列表 */
const getCitiesByProvince = (data: CityArea[], provinceCode: string): CityArea[] => {
  const province = data.find(p => p.code === provinceCode);
  return province?.children || [];
};

/** 根据城市编码获取区县列表 */
const getDistrictsByCity = (data: CityArea[], provinceCode: string, cityCode: string): CityArea[] => {
  const cities = getCitiesByProvince(data, provinceCode);
  const city = cities.find(c => c.code === cityCode);
  return city?.children || [];
};

// =====================================================
// 滚轮列选择组件
// =====================================================

interface WheelColumnProps {
  data: CityArea[];
  selectedCode: string;
  onSelect: (code: string) => void;
}

const VISIBLE_COUNT = 5;
const PADDING_ITEMS = Math.floor(VISIBLE_COUNT / 2);

const WheelColumn: React.FC<WheelColumnProps> = ({ data, selectedCode, onSelect }) => {
  const flatListRef = useRef<FlatList<CityArea>>(null);
  const isUserScrolling = useRef(false);

  // 选中项的索引
  const selectedIndex = useMemo(() => {
    const idx = data.findIndex(item => item.code === selectedCode);
    return idx >= 0 ? idx : 0;
  }, [data, selectedCode]);

  // 数据变化或选中项变化时，滚动到选中位置
  useEffect(() => {
    if (!isUserScrolling.current && flatListRef.current && data.length > 0) {
      const timer = setTimeout(() => {
        flatListRef.current?.scrollToOffset({
          offset: selectedIndex * ITEM_HEIGHT,
          animated: false,
        });
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [selectedIndex, data]);

  // 滚动结束时计算选中项
  const handleScrollEnd = useCallback((e: NativeSyntheticEvent<NativeScrollEvent>) => {
    const offsetY = e.nativeEvent.contentOffset.y;
    const index = Math.round(offsetY / ITEM_HEIGHT);
    const clampedIndex = Math.max(0, Math.min(index, data.length - 1));

    isUserScrolling.current = false;

    if (data[clampedIndex] && data[clampedIndex].code !== selectedCode) {
      onSelect(data[clampedIndex].code);
    }
  }, [data, selectedCode, onSelect]);

  const handleScrollBegin = useCallback(() => {
    isUserScrolling.current = true;
  }, []);

  // 渲染单个选项
  const renderItem = useCallback(({ item, index }: { item: CityArea; index: number }) => {
    const isSelected = index === selectedIndex;
    return (
      <TouchableOpacity
        activeOpacity={0.7}
        onPress={() => {
          onSelect(item.code);
          flatListRef.current?.scrollToOffset({
            offset: index * ITEM_HEIGHT,
            animated: true,
          });
        }}
        style={styles.wheelItem}
      >
        <Text
          style={[
            styles.wheelItemText,
            isSelected && styles.wheelItemTextSelected,
          ]}
          numberOfLines={1}
        >
          {item.name}
        </Text>
      </TouchableOpacity>
    );
  }, [selectedIndex, onSelect]);

  const keyExtractor = useCallback((item: CityArea) => item.code, []);

  const getItemLayout = useCallback((_: any, index: number) => ({
    length: ITEM_HEIGHT,
    offset: ITEM_HEIGHT * index,
    index,
  }), []);

  return (
    <View style={styles.wheelColumn}>
      {/* 选中行高亮 */}
      <View style={styles.selectedHighlight} pointerEvents="none" />
      <FlatList
        ref={flatListRef}
        data={data}
        renderItem={renderItem}
        keyExtractor={keyExtractor}
        getItemLayout={getItemLayout}
        showsVerticalScrollIndicator={false}
        snapToInterval={ITEM_HEIGHT}
        decelerationRate="fast"
        onScrollBeginDrag={handleScrollBegin}
        onMomentumScrollEnd={handleScrollEnd}
        onScrollEndDrag={(e) => {
          if (e.nativeEvent.velocity?.y === 0) {
            handleScrollEnd(e);
          }
        }}
        contentContainerStyle={{
          paddingTop: PADDING_ITEMS * ITEM_HEIGHT,
          paddingBottom: PADDING_ITEMS * ITEM_HEIGHT,
        }}
      />
    </View>
  );
};

// =====================================================
// 主组件实现
// =====================================================

const DbCityPicker = forwardRef<DbCityPickerRef, DbCityPickerProps>(({
  data,
  visible,
  onClose,
  onConfirm,
  title = '选择地区',
  defaultProvinceCode,
  defaultCityCode,
  defaultDistrictCode,
  confirmText = '确定',
  cancelText = '取消',
  confirmColor = '#007AFF',
  cancelColor = '#999999',
  style: containerStyle,
}, ref) => {
  // ==================== 状态管理 ====================

  const [selectedProvinceCode, setSelectedProvinceCode] = useState<string>('');
  const [selectedCityCode, setSelectedCityCode] = useState<string>('');
  const [selectedDistrictCode, setSelectedDistrictCode] = useState<string>('');

  // ==================== 计算属性 ====================

  const cities = useMemo(() => {
    return getCitiesByProvince(data, selectedProvinceCode);
  }, [data, selectedProvinceCode]);

  const districts = useMemo(() => {
    return getDistrictsByCity(data, selectedProvinceCode, selectedCityCode);
  }, [data, selectedProvinceCode, selectedCityCode]);

  // ==================== 初始化默认值 ====================

  useEffect(() => {
    if (visible) {
      if (defaultProvinceCode && defaultCityCode && defaultDistrictCode) {
        setSelectedProvinceCode(defaultProvinceCode);
        setSelectedCityCode(defaultCityCode);
        setSelectedDistrictCode(defaultDistrictCode);
      } else if (data.length > 0) {
        const firstProvince = data[0];
        const firstCity = firstProvince.children?.[0];
        const firstDistrict = firstCity?.children?.[0];

        setSelectedProvinceCode(firstProvince.code);
        setSelectedCityCode(firstCity?.code || '');
        setSelectedDistrictCode(firstDistrict?.code || '');
      }
    }
  }, [visible, defaultProvinceCode, defaultCityCode, defaultDistrictCode, data]);

  // ==================== 事件处理 ====================

  const handleProvinceChange = useCallback((code: string) => {
    setSelectedProvinceCode(code);
    const newCities = getCitiesByProvince(data, code);
    if (newCities.length > 0) {
      setSelectedCityCode(newCities[0].code);
      const newDistricts = newCities[0].children || [];
      setSelectedDistrictCode(newDistricts[0]?.code || '');
    } else {
      setSelectedCityCode('');
      setSelectedDistrictCode('');
    }
  }, [data]);

  const handleCityChange = useCallback((code: string) => {
    setSelectedCityCode(code);
    const newDistricts = getDistrictsByCity(data, selectedProvinceCode, code);
    setSelectedDistrictCode(newDistricts[0]?.code || '');
  }, [data, selectedProvinceCode]);

  const handleDistrictChange = useCallback((code: string) => {
    setSelectedDistrictCode(code);
  }, []);

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleConfirm = useCallback(() => {
    const province = data.find(p => p.code === selectedProvinceCode);
    if (!province) return;

    const city = province.children?.find(c => c.code === selectedCityCode);
    if (!city) return;

    const district = city.children?.find(d => d.code === selectedDistrictCode);
    if (!district) return;

    onConfirm({
      province,
      city,
      district,
      fullAddress: `${province.name} ${city.name} ${district.name}`,
    });
    onClose();
  }, [data, selectedProvinceCode, selectedCityCode, selectedDistrictCode, onConfirm, onClose]);

  // ==================== Ref 方法 ====================

  useImperativeHandle(ref, () => ({
    reset: () => {
      if (data.length > 0) {
        const firstProvince = data[0];
        const firstCity = firstProvince.children?.[0];
        const firstDistrict = firstCity?.children?.[0];

        setSelectedProvinceCode(firstProvince.code);
        setSelectedCityCode(firstCity?.code || '');
        setSelectedDistrictCode(firstDistrict?.code || '');
      }
    },
    getSelected: () => {
      const province = data.find(p => p.code === selectedProvinceCode);
      if (!province) return null;

      const city = province.children?.find(c => c.code === selectedCityCode);
      if (!city) return null;

      const district = city.children?.find(d => d.code === selectedDistrictCode);
      if (!district) return null;

      return {
        province,
        city,
        district,
        fullAddress: `${province.name} ${city.name} ${district.name}`,
      };
    },
  }), [data, selectedProvinceCode, selectedCityCode, selectedDistrictCode]);

  // ==================== 渲染 ====================

  return (
    <Modal
      visible={visible}
      transparent
      animationType="slide"
      onRequestClose={onClose}
    >
      <View style={styles.modalOverlay}>
        <TouchableOpacity
          style={styles.backdrop}
          activeOpacity={1}
          onPress={handleCancel}
        />

        <View style={[styles.container, containerStyle]}>
          {/* 头部 */}
          <View style={styles.header}>
            <TouchableOpacity style={styles.button} onPress={handleCancel}>
              <Text style={[styles.cancelButton, { color: cancelColor }]}>
                {cancelText}
              </Text>
            </TouchableOpacity>

            <Text style={styles.title}>{title}</Text>

            <TouchableOpacity style={styles.button} onPress={handleConfirm}>
              <Text style={[styles.confirmButton, { color: confirmColor }]}>
                {confirmText}
              </Text>
            </TouchableOpacity>
          </View>

          {/* 三列滚轮选择器 */}
          <View style={styles.pickerContainer}>
            <WheelColumn
              data={data}
              selectedCode={selectedProvinceCode}
              onSelect={handleProvinceChange}
            />
            <WheelColumn
              data={cities}
              selectedCode={selectedCityCode}
              onSelect={handleCityChange}
            />
            <WheelColumn
              data={districts}
              selectedCode={selectedDistrictCode}
              onSelect={handleDistrictChange}
            />
          </View>
        </View>
      </View>
    </Modal>
  );
});

DbCityPicker.displayName = 'DbCityPicker';

export default DbCityPicker;
export type { DbCityPickerProps, DbCityPickerRef, SelectedCity, CityArea } from './DbCityPicker.types';
